// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  TruckIcon,
  CurrencyDollarIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  DocumentArrowDownIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  BarsArrowDownIcon,
  FunnelIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// Sort types
type RegionSortField = 'efficiency' | 'revenue' | 'shipments' | 'onTime';
type CarrierSortField = 'shipments' | 'onTime' | 'avgCost' | 'rating';
type SortDirection = 'asc' | 'desc';

interface KPI {
  id: string;
  name: string;
  value: string;
  change: number;
  trend: 'up' | 'down' | 'stable';
  target: string;
  status: 'on_track' | 'at_risk' | 'behind';
  icon: React.ReactNode;
}

interface RegionData {
  region: string;
  shipments: number;
  onTime: number;
  revenue: number;
  cost: number;
  efficiency: number;
}

interface CarrierPerformance {
  carrier: string;
  shipments: number;
  onTime: number;
  avgCost: number;
  rating: number;
}

interface MonthlyMetric {
  month: string;
  shipments: number;
  revenue: number;
  cost: number;
  margin: number;
}

// Period-based data configurations
type PeriodKey = '1month' | '3months' | '6months' | '12months' | 'ytd';

interface PeriodDataSet {
  kpis: Omit<KPI, 'icon'>[];
  regions: RegionData[];
  carriers: CarrierPerformance[];
  monthly: MonthlyMetric[];
}

const periodData: Record<PeriodKey, PeriodDataSet> = {
  '1month': {
    kpis: [
      { id: 'kpi-1', name: 'Total Shipments', value: '2,156', change: 5.2, trend: 'up', target: '2,100', status: 'on_track' },
      { id: 'kpi-2', name: 'On-Time Delivery', value: '93.8%', change: -0.5, trend: 'down', target: '95%', status: 'at_risk' },
      { id: 'kpi-3', name: 'Revenue', value: '$720K', change: 8.1, trend: 'up', target: '$700K', status: 'on_track' },
      { id: 'kpi-4', name: 'Transportation Cost', value: '$285K', change: -2.1, trend: 'down', target: '$300K', status: 'on_track' },
      { id: 'kpi-5', name: 'Fill Rate', value: '96.2%', change: 0.8, trend: 'up', target: '97%', status: 'at_risk' },
      { id: 'kpi-6', name: 'Avg Lead Time', value: '4.5 days', change: -5.2, trend: 'down', target: '5 days', status: 'on_track' },
    ],
    regions: [
      { region: 'Midwest', shipments: 542, onTime: 94.5, revenue: 208000, cost: 81000, efficiency: 91 },
      { region: 'Northeast', shipments: 485, onTime: 93.2, revenue: 187000, cost: 74000, efficiency: 88 },
      { region: 'Southeast', shipments: 412, onTime: 95.8, revenue: 164000, cost: 61000, efficiency: 93 },
      { region: 'Southwest', shipments: 332, onTime: 91.8, revenue: 130000, cost: 52000, efficiency: 86 },
      { region: 'West Coast', shipments: 385, onTime: 94.1, revenue: 145000, cost: 57000, efficiency: 90 },
    ],
    carriers: [
      { carrier: 'Swift Logistics', shipments: 408, onTime: 96.2, avgCost: 1265, rating: 4.8 },
      { carrier: 'Great Lakes Transport', shipments: 315, onTime: 95.0, avgCost: 1195, rating: 4.6 },
      { carrier: 'Coastal Freight', shipments: 287, onTime: 94.5, avgCost: 1335, rating: 4.5 },
      { carrier: 'Mountain Freight Lines', shipments: 260, onTime: 93.2, avgCost: 1465, rating: 4.3 },
      { carrier: 'Desert Express', shipments: 223, onTime: 90.8, avgCost: 1165, rating: 4.1 },
      { carrier: 'Northeast Logistics', shipments: 213, onTime: 95.5, avgCost: 1395, rating: 4.7 },
    ],
    monthly: [
      { month: 'Week 1', shipments: 485, revenue: 162000, cost: 65000, margin: 60 },
      { month: 'Week 2', shipments: 520, revenue: 174000, cost: 68000, margin: 61 },
      { month: 'Week 3', shipments: 558, revenue: 186000, cost: 73000, margin: 61 },
      { month: 'Week 4', shipments: 593, revenue: 198000, cost: 79000, margin: 60 },
    ],
  },
  '3months': {
    kpis: [
      { id: 'kpi-1', name: 'Total Shipments', value: '6,842', change: 7.8, trend: 'up', target: '6,500', status: 'on_track' },
      { id: 'kpi-2', name: 'On-Time Delivery', value: '94.5%', change: 1.2, trend: 'up', target: '95%', status: 'at_risk' },
      { id: 'kpi-3', name: 'Revenue', value: '$2.3M', change: 10.5, trend: 'up', target: '$2.1M', status: 'on_track' },
      { id: 'kpi-4', name: 'Transportation Cost', value: '$920K', change: -3.8, trend: 'down', target: '$1.0M', status: 'on_track' },
      { id: 'kpi-5', name: 'Fill Rate', value: '96.5%', change: 1.0, trend: 'up', target: '97%', status: 'at_risk' },
      { id: 'kpi-6', name: 'Avg Lead Time', value: '4.3 days', change: -7.5, trend: 'down', target: '5 days', status: 'on_track' },
    ],
    regions: [
      { region: 'Midwest', shipments: 1720, onTime: 95.0, revenue: 665000, cost: 258000, efficiency: 92 },
      { region: 'Northeast', shipments: 1534, onTime: 93.5, revenue: 592000, cost: 236000, efficiency: 89 },
      { region: 'Southeast', shipments: 1305, onTime: 96.0, revenue: 521000, cost: 194000, efficiency: 94 },
      { region: 'Southwest', shipments: 1055, onTime: 92.2, revenue: 414000, cost: 165000, efficiency: 87 },
      { region: 'West Coast', shipments: 1228, onTime: 94.3, revenue: 462000, cost: 183000, efficiency: 91 },
    ],
    carriers: [
      { carrier: 'Swift Logistics', shipments: 1302, onTime: 96.4, avgCost: 1255, rating: 4.8 },
      { carrier: 'Great Lakes Transport', shipments: 1004, onTime: 95.1, avgCost: 1185, rating: 4.6 },
      { carrier: 'Coastal Freight', shipments: 914, onTime: 94.7, avgCost: 1325, rating: 4.5 },
      { carrier: 'Mountain Freight Lines', shipments: 829, onTime: 93.4, avgCost: 1455, rating: 4.3 },
      { carrier: 'Desert Express', shipments: 712, onTime: 91.0, avgCost: 1155, rating: 4.1 },
      { carrier: 'Northeast Logistics', shipments: 680, onTime: 95.7, avgCost: 1385, rating: 4.7 },
    ],
    monthly: [
      { month: 'Nov', shipments: 2150, revenue: 750000, cost: 285000, margin: 62 },
      { month: 'Dec', shipments: 2380, revenue: 820000, cost: 312000, margin: 62 },
      { month: 'Jan', shipments: 2312, revenue: 780000, cost: 298000, margin: 62 },
    ],
  },
  '6months': {
    kpis: [
      { id: 'kpi-1', name: 'Total Shipments', value: '12,458', change: 8.5, trend: 'up', target: '12,000', status: 'on_track' },
      { id: 'kpi-2', name: 'On-Time Delivery', value: '94.2%', change: 2.1, trend: 'up', target: '95%', status: 'at_risk' },
      { id: 'kpi-3', name: 'Revenue', value: '$4.2M', change: 12.3, trend: 'up', target: '$4.0M', status: 'on_track' },
      { id: 'kpi-4', name: 'Transportation Cost', value: '$1.8M', change: -3.5, trend: 'down', target: '$2.0M', status: 'on_track' },
      { id: 'kpi-5', name: 'Fill Rate', value: '96.8%', change: 1.2, trend: 'up', target: '97%', status: 'at_risk' },
      { id: 'kpi-6', name: 'Avg Lead Time', value: '4.2 days', change: -8.7, trend: 'down', target: '5 days', status: 'on_track' },
    ],
    regions: [
      { region: 'Midwest', shipments: 3245, onTime: 95.2, revenue: 1250000, cost: 485000, efficiency: 92 },
      { region: 'Northeast', shipments: 2890, onTime: 93.8, revenue: 1120000, cost: 445000, efficiency: 89 },
      { region: 'Southeast', shipments: 2456, onTime: 96.1, revenue: 980000, cost: 365000, efficiency: 94 },
      { region: 'Southwest', shipments: 1987, onTime: 92.5, revenue: 780000, cost: 310000, efficiency: 87 },
      { region: 'West Coast', shipments: 1880, onTime: 94.5, revenue: 870000, cost: 345000, efficiency: 91 },
    ],
    carriers: [
      { carrier: 'Swift Logistics', shipments: 2450, onTime: 96.5, avgCost: 1250, rating: 4.8 },
      { carrier: 'Great Lakes Transport', shipments: 1890, onTime: 95.2, avgCost: 1180, rating: 4.6 },
      { carrier: 'Coastal Freight', shipments: 1720, onTime: 94.8, avgCost: 1320, rating: 4.5 },
      { carrier: 'Mountain Freight Lines', shipments: 1560, onTime: 93.5, avgCost: 1450, rating: 4.3 },
      { carrier: 'Desert Express', shipments: 1340, onTime: 91.2, avgCost: 1150, rating: 4.1 },
      { carrier: 'Northeast Logistics', shipments: 1280, onTime: 95.8, avgCost: 1380, rating: 4.7 },
    ],
    monthly: [
      { month: 'Aug', shipments: 1850, revenue: 620000, cost: 248000, margin: 60 },
      { month: 'Sep', shipments: 1920, revenue: 680000, cost: 265000, margin: 61 },
      { month: 'Oct', shipments: 2150, revenue: 750000, cost: 285000, margin: 62 },
      { month: 'Nov', shipments: 2380, revenue: 820000, cost: 312000, margin: 62 },
      { month: 'Dec', shipments: 2890, revenue: 980000, cost: 365000, margin: 63 },
      { month: 'Jan', shipments: 2650, revenue: 890000, cost: 334000, margin: 62 },
    ],
  },
  '12months': {
    kpis: [
      { id: 'kpi-1', name: 'Total Shipments', value: '28,542', change: 11.2, trend: 'up', target: '26,000', status: 'on_track' },
      { id: 'kpi-2', name: 'On-Time Delivery', value: '93.8%', change: 1.5, trend: 'up', target: '94%', status: 'at_risk' },
      { id: 'kpi-3', name: 'Revenue', value: '$9.8M', change: 15.8, trend: 'up', target: '$9.0M', status: 'on_track' },
      { id: 'kpi-4', name: 'Transportation Cost', value: '$4.1M', change: -4.2, trend: 'down', target: '$4.5M', status: 'on_track' },
      { id: 'kpi-5', name: 'Fill Rate', value: '96.2%', change: 0.9, trend: 'up', target: '96%', status: 'on_track' },
      { id: 'kpi-6', name: 'Avg Lead Time', value: '4.4 days', change: -6.5, trend: 'down', target: '5 days', status: 'on_track' },
    ],
    regions: [
      { region: 'Midwest', shipments: 7420, onTime: 94.8, revenue: 2860000, cost: 1110000, efficiency: 91 },
      { region: 'Northeast', shipments: 6612, onTime: 93.2, revenue: 2562000, cost: 1018000, efficiency: 88 },
      { region: 'Southeast', shipments: 5620, onTime: 95.8, revenue: 2242000, cost: 835000, efficiency: 93 },
      { region: 'Southwest', shipments: 4545, onTime: 92.0, revenue: 1784000, cost: 709000, efficiency: 86 },
      { region: 'West Coast', shipments: 4345, onTime: 94.1, revenue: 1991000, cost: 789000, efficiency: 90 },
    ],
    carriers: [
      { carrier: 'Swift Logistics', shipments: 5605, onTime: 96.2, avgCost: 1242, rating: 4.8 },
      { carrier: 'Great Lakes Transport', shipments: 4324, onTime: 94.9, avgCost: 1172, rating: 4.6 },
      { carrier: 'Coastal Freight', shipments: 3935, onTime: 94.5, avgCost: 1312, rating: 4.5 },
      { carrier: 'Mountain Freight Lines', shipments: 3570, onTime: 93.2, avgCost: 1442, rating: 4.3 },
      { carrier: 'Desert Express', shipments: 3066, onTime: 90.9, avgCost: 1142, rating: 4.0 },
      { carrier: 'Northeast Logistics', shipments: 2928, onTime: 95.5, avgCost: 1372, rating: 4.7 },
    ],
    monthly: [
      { month: 'Feb', shipments: 1680, revenue: 545000, cost: 218000, margin: 60 },
      { month: 'Mar', shipments: 1780, revenue: 598000, cost: 239000, margin: 60 },
      { month: 'Apr', shipments: 1850, revenue: 620000, cost: 248000, margin: 60 },
      { month: 'May', shipments: 1920, revenue: 680000, cost: 265000, margin: 61 },
      { month: 'Jun', shipments: 2050, revenue: 715000, cost: 279000, margin: 61 },
      { month: 'Jul', shipments: 1920, revenue: 680000, cost: 265000, margin: 61 },
      { month: 'Aug', shipments: 1850, revenue: 620000, cost: 248000, margin: 60 },
      { month: 'Sep', shipments: 1920, revenue: 680000, cost: 265000, margin: 61 },
      { month: 'Oct', shipments: 2150, revenue: 750000, cost: 285000, margin: 62 },
      { month: 'Nov', shipments: 2380, revenue: 820000, cost: 312000, margin: 62 },
      { month: 'Dec', shipments: 2890, revenue: 980000, cost: 365000, margin: 63 },
      { month: 'Jan', shipments: 2650, revenue: 890000, cost: 334000, margin: 62 },
    ],
  },
  'ytd': {
    kpis: [
      { id: 'kpi-1', name: 'Total Shipments', value: '2,650', change: 4.8, trend: 'up', target: '2,500', status: 'on_track' },
      { id: 'kpi-2', name: 'On-Time Delivery', value: '94.8%', change: 2.5, trend: 'up', target: '95%', status: 'at_risk' },
      { id: 'kpi-3', name: 'Revenue', value: '$890K', change: 9.2, trend: 'up', target: '$850K', status: 'on_track' },
      { id: 'kpi-4', name: 'Transportation Cost', value: '$334K', change: -1.8, trend: 'down', target: '$360K', status: 'on_track' },
      { id: 'kpi-5', name: 'Fill Rate', value: '97.1%', change: 1.5, trend: 'up', target: '97%', status: 'on_track' },
      { id: 'kpi-6', name: 'Avg Lead Time', value: '4.1 days', change: -9.2, trend: 'down', target: '5 days', status: 'on_track' },
    ],
    regions: [
      { region: 'Midwest', shipments: 668, onTime: 95.5, revenue: 257000, cost: 100000, efficiency: 93 },
      { region: 'Northeast', shipments: 595, onTime: 94.2, revenue: 230000, cost: 92000, efficiency: 90 },
      { region: 'Southeast', shipments: 506, onTime: 96.4, revenue: 202000, cost: 75000, efficiency: 95 },
      { region: 'Southwest', shipments: 409, onTime: 93.0, revenue: 160000, cost: 64000, efficiency: 88 },
      { region: 'West Coast', shipments: 472, onTime: 94.8, revenue: 178000, cost: 71000, efficiency: 92 },
    ],
    carriers: [
      { carrier: 'Swift Logistics', shipments: 504, onTime: 96.8, avgCost: 1238, rating: 4.9 },
      { carrier: 'Great Lakes Transport', shipments: 389, onTime: 95.5, avgCost: 1168, rating: 4.7 },
      { carrier: 'Coastal Freight', shipments: 354, onTime: 95.0, avgCost: 1308, rating: 4.6 },
      { carrier: 'Mountain Freight Lines', shipments: 321, onTime: 93.8, avgCost: 1438, rating: 4.4 },
      { carrier: 'Desert Express', shipments: 276, onTime: 91.5, avgCost: 1138, rating: 4.2 },
      { carrier: 'Northeast Logistics', shipments: 264, onTime: 96.0, avgCost: 1368, rating: 4.8 },
    ],
    monthly: [
      { month: 'Jan', shipments: 2650, revenue: 890000, cost: 334000, margin: 62 },
    ],
  },
};

// KPI icons mapping (since icons can't be stored in plain data)
const kpiIcons: Record<string, React.ReactNode> = {
  'kpi-1': <TruckIcon className="w-6 h-6" />,
  'kpi-2': <ClockIcon className="w-6 h-6" />,
  'kpi-3': <CurrencyDollarIcon className="w-6 h-6" />,
  'kpi-4': <CurrencyDollarIcon className="w-6 h-6" />,
  'kpi-5': <CubeIcon className="w-6 h-6" />,
  'kpi-6': <ClockIcon className="w-6 h-6" />,
};

const mockKPIs: KPI[] = [
  {
    id: 'kpi-1',
    name: 'Total Shipments',
    value: '12,458',
    change: 8.5,
    trend: 'up',
    target: '12,000',
    status: 'on_track',
    icon: <TruckIcon className="w-6 h-6" />,
  },
  {
    id: 'kpi-2',
    name: 'On-Time Delivery',
    value: '94.2%',
    change: 2.1,
    trend: 'up',
    target: '95%',
    status: 'at_risk',
    icon: <ClockIcon className="w-6 h-6" />,
  },
  {
    id: 'kpi-3',
    name: 'Revenue',
    value: '$4.2M',
    change: 12.3,
    trend: 'up',
    target: '$4.0M',
    status: 'on_track',
    icon: <CurrencyDollarIcon className="w-6 h-6" />,
  },
  {
    id: 'kpi-4',
    name: 'Transportation Cost',
    value: '$1.8M',
    change: -3.5,
    trend: 'down',
    target: '$2.0M',
    status: 'on_track',
    icon: <CurrencyDollarIcon className="w-6 h-6" />,
  },
  {
    id: 'kpi-5',
    name: 'Fill Rate',
    value: '96.8%',
    change: 1.2,
    trend: 'up',
    target: '97%',
    status: 'at_risk',
    icon: <CubeIcon className="w-6 h-6" />,
  },
  {
    id: 'kpi-6',
    name: 'Avg Lead Time',
    value: '4.2 days',
    change: -8.7,
    trend: 'down',
    target: '5 days',
    status: 'on_track',
    icon: <ClockIcon className="w-6 h-6" />,
  },
];

const mockRegionData: RegionData[] = [
  { region: 'Midwest', shipments: 3245, onTime: 95.2, revenue: 1250000, cost: 485000, efficiency: 92 },
  { region: 'Northeast', shipments: 2890, onTime: 93.8, revenue: 1120000, cost: 445000, efficiency: 89 },
  { region: 'Southeast', shipments: 2456, onTime: 96.1, revenue: 980000, cost: 365000, efficiency: 94 },
  { region: 'Southwest', shipments: 1987, onTime: 92.5, revenue: 780000, cost: 310000, efficiency: 87 },
  { region: 'West Coast', shipments: 1880, onTime: 94.5, revenue: 870000, cost: 345000, efficiency: 91 },
];

const mockCarrierPerformance: CarrierPerformance[] = [
  { carrier: 'Swift Logistics', shipments: 2450, onTime: 96.5, avgCost: 1250, rating: 4.8 },
  { carrier: 'Great Lakes Transport', shipments: 1890, onTime: 95.2, avgCost: 1180, rating: 4.6 },
  { carrier: 'Coastal Freight', shipments: 1720, onTime: 94.8, avgCost: 1320, rating: 4.5 },
  { carrier: 'Mountain Freight Lines', shipments: 1560, onTime: 93.5, avgCost: 1450, rating: 4.3 },
  { carrier: 'Desert Express', shipments: 1340, onTime: 91.2, avgCost: 1150, rating: 4.1 },
  { carrier: 'Northeast Logistics', shipments: 1280, onTime: 95.8, avgCost: 1380, rating: 4.7 },
];

const mockMonthlyData: MonthlyMetric[] = [
  { month: 'Aug', shipments: 1850, revenue: 620000, cost: 248000, margin: 60 },
  { month: 'Sep', shipments: 1920, revenue: 680000, cost: 265000, margin: 61 },
  { month: 'Oct', shipments: 2150, revenue: 750000, cost: 285000, margin: 62 },
  { month: 'Nov', shipments: 2380, revenue: 820000, cost: 312000, margin: 62 },
  { month: 'Dec', shipments: 2890, revenue: 980000, cost: 365000, margin: 63 },
  { month: 'Jan', shipments: 2650, revenue: 890000, cost: 334000, margin: 62 },
];

const Analytics: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodKey>('6months');
  const [selectedMetric, setSelectedMetric] = useState<string>('shipments');
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
  const exportDropdownRef = useRef<HTMLDivElement>(null);

  // Regional Performance sorting
  const [regionSortField, setRegionSortField] = useState<RegionSortField>('efficiency');
  const [regionSortDirection, setRegionSortDirection] = useState<SortDirection>('desc');

  // Carrier Performance sorting and filtering
  const [carrierSortField, setCarrierSortField] = useState<CarrierSortField>('shipments');
  const [carrierSortDirection, setCarrierSortDirection] = useState<SortDirection>('desc');
  const [carrierFilterOpen, setCarrierFilterOpen] = useState(false);
  const [carrierOnTimeFilter, setCarrierOnTimeFilter] = useState<string>('');
  const [carrierRatingFilter, setCarrierRatingFilter] = useState<string>('');
  const carrierFilterRef = useRef<HTMLDivElement>(null);

  // AI Insights loading state
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [aiInsights, setAiInsights] = useState<{
    revenueGrowth: { title: string; bullets: string[] };
    onTimeRisk: { title: string; bullets: string[] };
    topPerformer: { title: string; bullets: string[] };
  } | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target as Node)) {
        setExportDropdownOpen(false);
      }
      if (carrierFilterRef.current && !carrierFilterRef.current.contains(event.target as Node)) {
        setCarrierFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate AI insights when period changes
  useEffect(() => {
    generateInsights();
  }, [selectedPeriod]);

  // Generate dynamic insights based on current data
  const generateInsights = () => {
    setInsightsLoading(true);

    // Simulate API call delay for realistic UX
    setTimeout(() => {
      const data = periodData[selectedPeriod];
      const revenueKpi = data.kpis.find(k => k.id === 'kpi-3');
      const onTimeKpi = data.kpis.find(k => k.id === 'kpi-2');

      // Find region with lowest on-time
      const lowestOnTimeRegion = [...data.regions].sort((a, b) => a.onTime - b.onTime)[0];
      const highestOnTimeRegion = [...data.regions].sort((a, b) => b.onTime - a.onTime)[0];

      // Find top performer by efficiency
      const topRegion = [...data.regions].sort((a, b) => b.efficiency - a.efficiency)[0];
      const topCarrier = [...data.carriers].sort((a, b) => b.onTime - a.onTime)[0];

      // Calculate total revenue and compare to target
      const totalRevenue = data.regions.reduce((acc, r) => acc + r.revenue, 0);
      const totalShipments = data.regions.reduce((acc, r) => acc + r.shipments, 0);

      // Calculate averages
      const avgOnTime = data.regions.reduce((acc, r) => acc + r.onTime, 0) / data.regions.length;
      const targetOnTime = 95;
      const onTimeGap = targetOnTime - avgOnTime;

      setAiInsights({
        revenueGrowth: {
          title: 'Revenue Growth',
          bullets: [
            `Total revenue of $${(totalRevenue / 1000000).toFixed(1)}M represents a ${revenueKpi?.change || 0}% increase vs. prior period`,
            `${topRegion.region} region contributed ${((topRegion.revenue / totalRevenue) * 100).toFixed(1)}% of total revenue with $${(topRegion.revenue / 1000).toFixed(0)}K`,
            `Average revenue per shipment: $${(totalRevenue / totalShipments).toFixed(0)} across ${totalShipments.toLocaleString()} shipments`,
          ],
        },
        onTimeRisk: {
          title: 'On-Time Delivery Risk',
          bullets: [
            `${lowestOnTimeRegion.region} region at ${lowestOnTimeRegion.onTime}% is ${(targetOnTime - lowestOnTimeRegion.onTime).toFixed(1)}% below the ${targetOnTime}% target`,
            `Network average of ${avgOnTime.toFixed(1)}% on-time delivery ${onTimeGap > 0 ? `needs ${onTimeGap.toFixed(1)}% improvement` : 'meets target'}`,
            `Consider reallocating shipments from ${lowestOnTimeRegion.region} to ${highestOnTimeRegion.region} (${highestOnTimeRegion.onTime}% on-time)`,
          ],
        },
        topPerformer: {
          title: 'Top Performer',
          bullets: [
            `${topRegion.region} leads with ${topRegion.efficiency}% efficiency and ${topRegion.onTime}% on-time delivery`,
            `${topCarrier.carrier} is the top carrier with ${topCarrier.onTime}% on-time rate across ${topCarrier.shipments.toLocaleString()} shipments`,
            `Best practices from ${topRegion.region} could improve network efficiency by ${(topRegion.efficiency - (data.regions.reduce((acc, r) => acc + r.efficiency, 0) / data.regions.length)).toFixed(1)}%`,
          ],
        },
      });

      setInsightsLoading(false);
    }, 500);
  };

  // Get data for selected period
  const currentData = useMemo(() => {
    const data = periodData[selectedPeriod];
    return {
      kpis: data.kpis.map(kpi => ({ ...kpi, icon: kpiIcons[kpi.id] })) as KPI[],
      regions: data.regions,
      carriers: data.carriers,
      monthly: data.monthly,
    };
  }, [selectedPeriod]);

  // Calculate totals from current period data
  const totalStats = useMemo(() => ({
    totalShipments: currentData.regions.reduce((acc, r) => acc + r.shipments, 0),
    totalRevenue: currentData.regions.reduce((acc, r) => acc + r.revenue, 0),
    avgOnTime: Math.round((currentData.regions.reduce((acc, r) => acc + r.onTime, 0) / currentData.regions.length) * 10) / 10,
    avgEfficiency: Math.round((currentData.regions.reduce((acc, r) => acc + r.efficiency, 0) / currentData.regions.length) * 10) / 10,
  }), [currentData.regions]);

  // Sorted regions
  const sortedRegions = useMemo(() => {
    return [...currentData.regions].sort((a, b) => {
      const aVal = a[regionSortField];
      const bVal = b[regionSortField];
      return regionSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [currentData.regions, regionSortField, regionSortDirection]);

  // Filtered and sorted carriers
  const filteredAndSortedCarriers = useMemo(() => {
    let filtered = [...currentData.carriers];

    // Apply on-time filter
    if (carrierOnTimeFilter) {
      const threshold = parseFloat(carrierOnTimeFilter);
      filtered = filtered.filter(c => c.onTime >= threshold);
    }

    // Apply rating filter
    if (carrierRatingFilter) {
      const threshold = parseFloat(carrierRatingFilter);
      filtered = filtered.filter(c => c.rating >= threshold);
    }

    // Sort
    return filtered.sort((a, b) => {
      const aVal = a[carrierSortField];
      const bVal = b[carrierSortField];
      return carrierSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [currentData.carriers, carrierSortField, carrierSortDirection, carrierOnTimeFilter, carrierRatingFilter]);

  // Region sort handler
  const handleRegionSort = (field: RegionSortField) => {
    if (regionSortField === field) {
      setRegionSortDirection(regionSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setRegionSortField(field);
      setRegionSortDirection('desc');
    }
  };

  // Carrier sort handler
  const handleCarrierSort = (field: CarrierSortField) => {
    if (carrierSortField === field) {
      setCarrierSortDirection(carrierSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCarrierSortField(field);
      setCarrierSortDirection('desc');
    }
  };

  // Check if any carrier filters are active
  const hasActiveCarrierFilters = carrierOnTimeFilter || carrierRatingFilter;

  // Chart calculations
  const maxShipments = Math.max(...currentData.monthly.map((d) => d.shipments));
  const maxRevenue = Math.max(...currentData.monthly.map((d) => d.revenue));

  // Period label for display
  const periodLabels: Record<PeriodKey, string> = {
    '1month': 'Last Month',
    '3months': 'Last 3 Months',
    '6months': 'Last 6 Months',
    '12months': 'Last 12 Months',
    'ytd': 'Year to Date',
  };

  // Export handlers
  const handleExportCSV = () => {
    const headers = ['Metric', 'Value', 'Change', 'Target', 'Status'];
    const kpiRows = currentData.kpis.map(kpi => [
      kpi.name,
      kpi.value,
      `${kpi.change > 0 ? '+' : ''}${kpi.change}%`,
      kpi.target,
      kpi.status.replace('_', ' '),
    ]);

    const regionHeaders = ['Region', 'Shipments', 'On-Time %', 'Revenue', 'Efficiency'];
    const regionRows = currentData.regions.map(r => [
      r.region,
      r.shipments.toString(),
      `${r.onTime}%`,
      `$${r.revenue.toLocaleString()}`,
      `${r.efficiency}%`,
    ]);

    const carrierHeaders = ['Carrier', 'Shipments', 'On-Time %', 'Avg Cost', 'Rating'];
    const carrierRows = currentData.carriers.map(c => [
      c.carrier,
      c.shipments.toString(),
      `${c.onTime}%`,
      `$${c.avgCost}`,
      c.rating.toString(),
    ]);

    const csvContent = [
      `Analytics Report - ${periodLabels[selectedPeriod]}`,
      '',
      'KEY PERFORMANCE INDICATORS',
      headers.join(','),
      ...kpiRows.map(row => row.join(',')),
      '',
      'REGIONAL PERFORMANCE',
      regionHeaders.join(','),
      ...regionRows.map(row => row.join(',')),
      '',
      'CARRIER PERFORMANCE',
      carrierHeaders.join(','),
      ...carrierRows.map(row => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-report-${selectedPeriod}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportDropdownOpen(false);
  };

  const handleExportWord = () => {
    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word'>
      <head><meta charset="utf-8"><title>Analytics Report</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #0d9488; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
        h2 { color: #374151; margin-top: 30px; }
        table { border-collapse: collapse; width: 100%; margin: 15px 0; }
        th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; }
        th { background-color: #f3f4f6; font-weight: bold; }
        .on-track { color: #059669; }
        .at-risk { color: #d97706; }
        .behind { color: #dc2626; }
      </style>
      </head>
      <body>
        <h1>Analytics Report - ${periodLabels[selectedPeriod]}</h1>
        <p>Generated on ${new Date().toLocaleDateString()}</p>

        <h2>Key Performance Indicators</h2>
        <table>
          <tr><th>Metric</th><th>Value</th><th>Change</th><th>Target</th><th>Status</th></tr>
          ${currentData.kpis.map(kpi => `
            <tr>
              <td>${kpi.name}</td>
              <td><strong>${kpi.value}</strong></td>
              <td>${kpi.change > 0 ? '+' : ''}${kpi.change}%</td>
              <td>${kpi.target}</td>
              <td class="${kpi.status.replace('_', '-')}">${kpi.status.replace('_', ' ').toUpperCase()}</td>
            </tr>
          `).join('')}
        </table>

        <h2>Regional Performance</h2>
        <table>
          <tr><th>Region</th><th>Shipments</th><th>On-Time %</th><th>Revenue</th><th>Efficiency</th></tr>
          ${currentData.regions.map(r => `
            <tr>
              <td>${r.region}</td>
              <td>${r.shipments.toLocaleString()}</td>
              <td>${r.onTime}%</td>
              <td>$${r.revenue.toLocaleString()}</td>
              <td>${r.efficiency}%</td>
            </tr>
          `).join('')}
        </table>

        <h2>Carrier Performance</h2>
        <table>
          <tr><th>Rank</th><th>Carrier</th><th>Shipments</th><th>On-Time %</th><th>Avg Cost</th><th>Rating</th></tr>
          ${currentData.carriers.map((c, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${c.carrier}</td>
              <td>${c.shipments.toLocaleString()}</td>
              <td>${c.onTime}%</td>
              <td>$${c.avgCost.toLocaleString()}</td>
              <td>${c.rating}</td>
            </tr>
          `).join('')}
        </table>

        <h2>Summary</h2>
        <table>
          <tr><th>Metric</th><th>Value</th></tr>
          <tr><td>Total Shipments</td><td>${totalStats.totalShipments.toLocaleString()}</td></tr>
          <tr><td>Total Revenue</td><td>$${(totalStats.totalRevenue / 1000000).toFixed(1)}M</td></tr>
          <tr><td>Average On-Time Delivery</td><td>${totalStats.avgOnTime}%</td></tr>
          <tr><td>Average Network Efficiency</td><td>${totalStats.avgEfficiency}%</td></tr>
        </table>
      </body>
      </html>
    `;
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-report-${selectedPeriod}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setExportDropdownOpen(false);
  };

  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Analytics Report - ${periodLabels[selectedPeriod]}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          h1 { color: #0d9488; border-bottom: 2px solid #0d9488; padding-bottom: 10px; }
          h2 { color: #374151; margin-top: 30px; page-break-after: avoid; }
          table { border-collapse: collapse; width: 100%; margin: 15px 0; }
          th, td { border: 1px solid #d1d5db; padding: 8px 12px; text-align: left; font-size: 12px; }
          th { background-color: #f3f4f6; font-weight: bold; }
          .on-track { color: #059669; }
          .at-risk { color: #d97706; }
          .behind { color: #dc2626; }
          .summary-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin: 20px 0; }
          .summary-card { background: #f9fafb; padding: 15px; border-radius: 8px; text-align: center; }
          .summary-value { font-size: 24px; font-weight: bold; color: #0d9488; }
          .summary-label { color: #6b7280; font-size: 12px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        <h1>Analytics Report</h1>
        <p><strong>Period:</strong> ${periodLabels[selectedPeriod]} | <strong>Generated:</strong> ${new Date().toLocaleDateString()}</p>

        <div class="summary-grid">
          <div class="summary-card">
            <div class="summary-value">${totalStats.totalShipments.toLocaleString()}</div>
            <div class="summary-label">Total Shipments</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">$${(totalStats.totalRevenue / 1000000).toFixed(1)}M</div>
            <div class="summary-label">Total Revenue</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">${totalStats.avgOnTime}%</div>
            <div class="summary-label">On-Time Delivery</div>
          </div>
          <div class="summary-card">
            <div class="summary-value">${totalStats.avgEfficiency}%</div>
            <div class="summary-label">Network Efficiency</div>
          </div>
        </div>

        <h2>Key Performance Indicators</h2>
        <table>
          <tr><th>Metric</th><th>Value</th><th>Change</th><th>Target</th><th>Status</th></tr>
          ${currentData.kpis.map(kpi => `
            <tr>
              <td>${kpi.name}</td>
              <td><strong>${kpi.value}</strong></td>
              <td>${kpi.change > 0 ? '+' : ''}${kpi.change}%</td>
              <td>${kpi.target}</td>
              <td class="${kpi.status.replace('_', '-')}">${kpi.status.replace('_', ' ').toUpperCase()}</td>
            </tr>
          `).join('')}
        </table>

        <h2>Regional Performance</h2>
        <table>
          <tr><th>Region</th><th>Shipments</th><th>On-Time %</th><th>Revenue</th><th>Efficiency</th></tr>
          ${currentData.regions.map(r => `
            <tr>
              <td>${r.region}</td>
              <td>${r.shipments.toLocaleString()}</td>
              <td>${r.onTime}%</td>
              <td>$${r.revenue.toLocaleString()}</td>
              <td>${r.efficiency}%</td>
            </tr>
          `).join('')}
        </table>

        <h2>Carrier Performance</h2>
        <table>
          <tr><th>Rank</th><th>Carrier</th><th>Shipments</th><th>On-Time %</th><th>Avg Cost</th><th>Rating</th></tr>
          ${currentData.carriers.map((c, i) => `
            <tr>
              <td>${i + 1}</td>
              <td>${c.carrier}</td>
              <td>${c.shipments.toLocaleString()}</td>
              <td>${c.onTime}%</td>
              <td>$${c.avgCost.toLocaleString()}</td>
              <td>${c.rating}</td>
            </tr>
          `).join('')}
        </table>

        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
    setExportDropdownOpen(false);
  };

  const getStatusIcon = (status: KPI['status']) => {
    switch (status) {
      case 'on_track':
        return (
          <div className="relative group cursor-help" title="On Track - Meeting or exceeding target">
            <CheckCircleIcon className="w-5 h-5 text-green-500" />
            <div className="invisible group-hover:visible absolute top-full right-0 mt-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg whitespace-nowrap z-[100]">
              On Track - Meeting or exceeding target
              <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700"></div>
            </div>
          </div>
        );
      case 'at_risk':
        return (
          <div className="relative group cursor-help" title="At Risk - Below target, needs attention">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />
            <div className="invisible group-hover:visible absolute top-full right-0 mt-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg whitespace-nowrap z-[100]">
              At Risk - Below target, needs attention
              <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700"></div>
            </div>
          </div>
        );
      case 'behind':
        return (
          <div className="relative group cursor-help" title="Behind - Significantly below target">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
            <div className="invisible group-hover:visible absolute top-full right-0 mt-1 px-2 py-1 text-xs font-medium text-white bg-gray-900 dark:bg-gray-700 rounded shadow-lg whitespace-nowrap z-[100]">
              Behind - Significantly below target
              <div className="absolute bottom-full right-2 border-4 border-transparent border-b-gray-900 dark:border-b-gray-700"></div>
            </div>
          </div>
        );
    }
  };

  const getStatusColor = (status: KPI['status']) => {
    switch (status) {
      case 'on_track':
        return 'text-green-600 dark:text-green-400';
      case 'at_risk':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'behind':
        return 'text-red-600 dark:text-red-400';
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable', isPositiveGood: boolean = true) => {
    if (trend === 'stable') return 'text-gray-500';
    if ((trend === 'up' && isPositiveGood) || (trend === 'down' && !isPositiveGood)) {
      return 'text-green-600 dark:text-green-400';
    }
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Dashboard</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Comprehensive logistics performance insights and reporting
          </p>
        </div>
        <div className="flex gap-2">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as PeriodKey)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-teal-500"
          >
            <option value="1month">Last Month</option>
            <option value="3months">Last 3 Months</option>
            <option value="6months">Last 6 Months</option>
            <option value="12months">Last 12 Months</option>
            <option value="ytd">Year to Date</option>
          </select>
          {/* Export Dropdown */}
          <div className="relative" ref={exportDropdownRef}>
            <button
              onClick={() => setExportDropdownOpen(!exportDropdownOpen)}
              className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export Report
              <ChevronDownIcon className="w-4 h-4 ml-2" />
            </button>
            {exportDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <button
                  onClick={handleExportCSV}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-t-lg"
                >
                  Export as CSV
                </button>
                <button
                  onClick={handleExportWord}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Export as Word
                </button>
                <button
                  onClick={handleExportPDF}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-b-lg"
                >
                  Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {currentData.kpis.map((kpi) => (
          <div
            key={kpi.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 overflow-visible"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400">
                {kpi.icon}
              </div>
              {getStatusIcon(kpi.status)}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{kpi.name}</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white mb-1">{kpi.value}</p>
            <div className="flex items-center justify-between">
              <span className={`text-xs font-medium flex items-center ${getTrendColor(kpi.trend, kpi.id !== 'kpi-4')}`}>
                {kpi.trend === 'up' ? (
                  <ArrowTrendingUpIcon className="w-3 h-3 mr-1" />
                ) : kpi.trend === 'down' ? (
                  <ArrowTrendingDownIcon className="w-3 h-3 mr-1" />
                ) : null}
                {kpi.change > 0 ? '+' : ''}{kpi.change}%
              </span>
              <span className="text-xs text-gray-400">Target: {kpi.target}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Monthly Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Shipments and revenue trend</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedMetric('shipments')}
                className={`px-3 py-1 rounded-lg text-xs font-medium ${
                  selectedMetric === 'shipments'
                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Shipments
              </button>
              <button
                onClick={() => setSelectedMetric('revenue')}
                className={`px-3 py-1 rounded-lg text-xs font-medium ${
                  selectedMetric === 'revenue'
                    ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                }`}
              >
                Revenue
              </button>
            </div>
          </div>

          <div className="relative h-64">
            <div className="absolute inset-0 flex items-end justify-between gap-2">
              {currentData.monthly.map((month) => (
                <div key={month.month} className="flex-1 flex flex-col items-center">
                  <div className="w-full flex flex-col items-center relative h-48">
                    <div
                      className="absolute bottom-0 w-full max-w-12 bg-gradient-to-t from-teal-500 to-teal-400 rounded-t-lg"
                      style={{
                        height: `${selectedMetric === 'shipments'
                          ? (month.shipments / maxShipments) * 100
                          : (month.revenue / maxRevenue) * 100}%`,
                      }}
                    ></div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 mt-2">{month.month}</span>
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    {selectedMetric === 'shipments'
                      ? month.shipments.toLocaleString()
                      : `$${(month.revenue / 1000).toFixed(0)}K`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Regional Performance */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Regional Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Performance by geographic region</p>
            </div>
          </div>

          {/* Sort Controls */}
          <div className="flex items-center gap-2 mb-4 text-sm">
            <BarsArrowDownIcon className="w-4 h-4 text-gray-500" />
            <span className="text-gray-500 dark:text-gray-400">Sort by:</span>
            <div className="flex flex-wrap gap-1">
              {[
                { field: 'efficiency' as RegionSortField, label: 'Efficiency' },
                { field: 'revenue' as RegionSortField, label: 'Revenue' },
                { field: 'shipments' as RegionSortField, label: 'Shipments' },
                { field: 'onTime' as RegionSortField, label: 'On-Time' },
              ].map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => handleRegionSort(field)}
                  className={`inline-flex items-center px-2 py-1 rounded text-xs transition-colors ${
                    regionSortField === field
                      ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {label}
                  {regionSortField === field && (
                    regionSortDirection === 'desc'
                      ? <ChevronDownIcon className="w-3 h-3 ml-1" />
                      : <ChevronUpIcon className="w-3 h-3 ml-1" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {sortedRegions.map((region) => (
              <div key={region.region} className="flex items-center gap-4">
                <div className="w-24 text-sm font-medium text-gray-900 dark:text-white">{region.region}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-teal-500 rounded-full"
                        style={{ width: `${region.efficiency}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 w-12">
                      {region.efficiency}%
                    </span>
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span>{region.shipments.toLocaleString()} shipments</span>
                    <span>{region.onTime}% on-time</span>
                    <span>${(region.revenue / 1000).toFixed(0)}K revenue</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Carrier Performance Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Carrier Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredAndSortedCarriers.length} of {currentData.carriers.length} carriers
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Sort Controls */}
              <div className="flex items-center gap-1 text-sm">
                <BarsArrowDownIcon className="w-4 h-4 text-gray-500" />
                <div className="flex gap-1">
                  {[
                    { field: 'shipments' as CarrierSortField, label: 'Volume' },
                    { field: 'onTime' as CarrierSortField, label: 'On-Time' },
                    { field: 'avgCost' as CarrierSortField, label: 'Cost' },
                    { field: 'rating' as CarrierSortField, label: 'Rating' },
                  ].map(({ field, label }) => (
                    <button
                      key={field}
                      onClick={() => handleCarrierSort(field)}
                      className={`inline-flex items-center px-2 py-1 rounded text-xs transition-colors ${
                        carrierSortField === field
                          ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {label}
                      {carrierSortField === field && (
                        carrierSortDirection === 'desc'
                          ? <ChevronDownIcon className="w-3 h-3 ml-1" />
                          : <ChevronUpIcon className="w-3 h-3 ml-1" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filter Dropdown */}
              <div className="relative" ref={carrierFilterRef}>
                <button
                  onClick={() => setCarrierFilterOpen(!carrierFilterOpen)}
                  className={`inline-flex items-center px-3 py-1.5 border rounded-lg text-sm transition-colors ${
                    hasActiveCarrierFilters
                      ? 'border-teal-500 bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 dark:border-teal-500'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <FunnelIcon className="w-4 h-4 mr-2" />
                  Filter
                  {hasActiveCarrierFilters && (
                    <span className="ml-2 inline-flex items-center justify-center w-4 h-4 text-xs font-bold text-white bg-teal-600 rounded-full">
                      {[carrierOnTimeFilter, carrierRatingFilter].filter(Boolean).length}
                    </span>
                  )}
                </button>
                {carrierFilterOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 p-4">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min On-Time %
                        </label>
                        <select
                          value={carrierOnTimeFilter}
                          onChange={(e) => setCarrierOnTimeFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="">Any</option>
                          <option value="95">95%+</option>
                          <option value="93">93%+</option>
                          <option value="90">90%+</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Min Rating
                        </label>
                        <select
                          value={carrierRatingFilter}
                          onChange={(e) => setCarrierRatingFilter(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500"
                        >
                          <option value="">Any</option>
                          <option value="4.5">4.5+</option>
                          <option value="4.0">4.0+</option>
                          <option value="3.5">3.5+</option>
                        </select>
                      </div>
                      <button
                        onClick={() => {
                          setCarrierOnTimeFilter('');
                          setCarrierRatingFilter('');
                        }}
                        className="w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Carrier
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Shipments
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  On-Time %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avg Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Rating
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedCarriers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    No carriers match the current filters
                  </td>
                </tr>
              ) : filteredAndSortedCarriers.map((carrier, index) => (
                <tr key={carrier.carrier} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      index === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                      index === 1 ? 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200' :
                      index === 2 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' :
                      'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {index + 1}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mr-3">
                        <TruckIcon className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      </div>
                      <span className="text-sm font-medium text-gray-900 dark:text-white">{carrier.carrier}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">{carrier.shipments.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-16 h-2 bg-gray-200 dark:bg-gray-700 rounded-full mr-2 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${
                            carrier.onTime >= 95 ? 'bg-green-500' :
                            carrier.onTime >= 93 ? 'bg-blue-500' :
                            'bg-yellow-500'
                          }`}
                          style={{ width: `${carrier.onTime}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${
                        carrier.onTime >= 95 ? 'text-green-600 dark:text-green-400' :
                        carrier.onTime >= 93 ? 'text-blue-600 dark:text-blue-400' :
                        'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {carrier.onTime}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900 dark:text-white">${carrier.avgCost.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 dark:text-white mr-1">{carrier.rating}</span>
                      <svg className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <TruckIcon className="w-8 h-8 opacity-80" />
            <span className="text-xs px-2 py-1 bg-white/20 rounded-full">This Period</span>
          </div>
          <p className="text-3xl font-bold mb-1">{totalStats.totalShipments.toLocaleString()}</p>
          <p className="text-teal-100 text-sm">Total Shipments</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <CurrencyDollarIcon className="w-8 h-8 opacity-80" />
            <span className="text-xs px-2 py-1 bg-white/20 rounded-full">This Period</span>
          </div>
          <p className="text-3xl font-bold mb-1">${(totalStats.totalRevenue / 1000000).toFixed(1)}M</p>
          <p className="text-blue-100 text-sm">Total Revenue</p>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <CheckCircleIcon className="w-8 h-8 opacity-80" />
            <span className="text-xs px-2 py-1 bg-white/20 rounded-full">Average</span>
          </div>
          <p className="text-3xl font-bold mb-1">{totalStats.avgOnTime}%</p>
          <p className="text-green-100 text-sm">On-Time Delivery</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <ChartBarIcon className="w-8 h-8 opacity-80" />
            <span className="text-xs px-2 py-1 bg-white/20 rounded-full">Average</span>
          </div>
          <p className="text-3xl font-bold mb-1">{totalStats.avgEfficiency}%</p>
          <p className="text-purple-100 text-sm">Network Efficiency</p>
        </div>
      </div>

      {/* Key Insights */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Key Insights</h2>
            <span className="text-xs px-2 py-0.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-full">
              {periodLabels[selectedPeriod]}
            </span>
          </div>
          <button
            onClick={generateInsights}
            disabled={insightsLoading}
            className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${insightsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {insightsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="p-4 bg-gray-50 dark:bg-gray-900/20 rounded-lg animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : aiInsights ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Revenue Growth */}
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">{aiInsights.revenueGrowth.title}</p>
              </div>
              <ul className="space-y-2 ml-11">
                {aiInsights.revenueGrowth.bullets.map((bullet, i) => (
                  <li key={i} className="text-sm text-green-700 dark:text-green-400 flex items-start gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>

            {/* On-Time Risk */}
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                </div>
                <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">{aiInsights.onTimeRisk.title}</p>
              </div>
              <ul className="space-y-2 ml-11">
                {aiInsights.onTimeRisk.bullets.map((bullet, i) => (
                  <li key={i} className="text-sm text-yellow-700 dark:text-yellow-400 flex items-start gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>

            {/* Top Performer */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex items-start gap-3 mb-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <MapPinIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm font-medium text-blue-800 dark:text-blue-300">{aiInsights.topPerformer.title}</p>
              </div>
              <ul className="space-y-2 ml-11">
                {aiInsights.topPerformer.bullets.map((bullet, i) => (
                  <li key={i} className="text-sm text-blue-700 dark:text-blue-400 flex items-start gap-2">
                    <span className="inline-block w-1.5 h-1.5 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></span>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <SparklesIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Click Refresh to generate insights</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
